import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Mic, MicOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Speech-to-text hook using the Web Speech API
 * @param {function} onTranscript - Callback when transcript is received
 * @param {object} options - Configuration options
 * @param {boolean} options.continuous - Keep listening continuously until manually stopped
 * @param {boolean} options.autoRestart - Auto-restart on silence (extends listening time)
 */
export const useSpeechToText = (onTranscript, options = {}) => {
  const { continuous = true, autoRestart = true } = options;
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const isManualStop = useRef(false);
  const restartTimeoutRef = useRef(null);
  
  // Check for browser support on initial render
  const isSupported = typeof window !== 'undefined' && 
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = continuous;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      // Increase max silence - some browsers respect this
      recognitionRef.current.maxAlternatives = 1;

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          }
        }

        if (finalTranscript && onTranscript) {
          onTranscript(finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
          isManualStop.current = true; // Don't auto-restart on permission error
        } else if (event.error === 'aborted') {
          // User or system aborted - don't show error
        } else if (event.error !== 'no-speech') {
          toast.error(`Speech recognition error: ${event.error}`);
        }
        // Don't set isListening to false here - let onend handle it
      };

      recognitionRef.current.onend = () => {
        // Auto-restart if not manually stopped and autoRestart is enabled
        if (!isManualStop.current && autoRestart && isListening) {
          // Small delay before restarting to prevent rapid cycling
          restartTimeoutRef.current = setTimeout(() => {
            if (recognitionRef.current && !isManualStop.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                // Already started or other error
                setIsListening(false);
              }
            }
          }, 100);
        } else {
          setIsListening(false);
        }
      };
    }

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        isManualStop.current = true;
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript, continuous, autoRestart, isListening]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isListening) {
      try {
        isManualStop.current = false;
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
      }
    }
  }, [isListening]);

  const stopListening = useCallback(() => {
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
    }
    isManualStop.current = true;
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    startListening,
    stopListening,
    toggleListening
  };
};

/**
 * Speech-to-text button component
 * @param {function} onTranscript - Callback when speech is transcribed (receives text to append)
 * @param {string} className - Additional CSS classes
 * @param {string} size - Button size ('sm', 'default', 'lg')
 */
const SpeechToTextButton = ({ onTranscript, className = '', size = 'sm' }) => {
  const { isListening, isSupported, toggleListening } = useSpeechToText(onTranscript);

  if (!isSupported) {
    return null; // Don't render if not supported
  }

  return (
    <Button
      type="button"
      variant={isListening ? 'destructive' : 'outline'}
      size={size}
      onClick={toggleListening}
      className={`${className} ${isListening ? 'animate-pulse' : ''}`}
      title={isListening ? 'Click to stop' : 'Click to speak'}
    >
      {isListening ? (
        <>
          <MicOff className="w-4 h-4 mr-1" />
          <span className="text-xs">Stop</span>
        </>
      ) : (
        <>
          <Mic className="w-4 h-4 mr-1" />
          <span className="text-xs">Voice</span>
        </>
      )}
    </Button>
  );
};

/**
 * Compact microphone icon button (no text)
 */
export const SpeechToTextIcon = ({ onTranscript, className = '' }) => {
  const { isListening, isSupported, toggleListening } = useSpeechToText(onTranscript);

  if (!isSupported) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={toggleListening}
      className={`p-2 rounded-full transition-all ${
        isListening 
          ? 'bg-red-500 text-white animate-pulse' 
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
      } ${className}`}
      title={isListening ? 'Click to stop' : 'Click to speak'}
    >
      {isListening ? (
        <MicOff className="w-5 h-5" />
      ) : (
        <Mic className="w-5 h-5" />
      )}
    </button>
  );
};

export default SpeechToTextButton;
